import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { NewTradeForm } from '@/components/trades/NewTradeForm'
import { CloseTradeForm } from '@/components/trades/CloseTradeForm'
import { CashControlsSection } from '../components/trades/CashControlsSection'
import { PositionsSection } from '@/components/trades/PositionsSection'
import { PremiumByTickerModal } from '@/components/trades/PremiumByTickerModal'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { EarningsBadge } from '@/components/shared/EarningsBadge'
import { TimePeriodFilter, filterTradesByPeriod } from '@/components/shared/TimePeriodFilter'
import { useColumnOrder } from '@/hooks/useColumnOrder'
import { invokeEdgeFunction } from '@/lib/edgeFunctions'
import { ACCOUNT_NAMES, CLOSE_ACTIONS, formatDateShort } from '@/lib/constants'
import { useAppStore } from '@/store/appStore'
import { supabase } from '@/lib/supabase'
import { useMobileNav } from '@/lib/mobileNav'
import {
  formatTradeField,
  DETAIL_FIELDS,
  CLOSED_DETAIL_FIELDS,
  TABLET_ROW_FIELDS,
  EDITABLE_CARD_FIELDS,
  rawFieldValue,
  type TradeLike,
} from '@/lib/tradeCardFields'
import { TradeCard } from '@/components/trades/TradeCard'
import type { Position } from '@/types'

const USER_ID = '00000000-0000-0000-0000-000000000000'

interface Trade {
  id: string
  ticker: string
  action: string
  priceWhenBought: number
  strikePrice: number
  currentPrice: number
  status: string
  buyDate: string
  expDate: string
  closingMonth: string
  originalDays: number
  daysLeft: number
  daysKept: number | null
  premium: number
  qty: number
  commission: number
  fees: number
  total: number
  closeAction: string | null
  closeDate: string | null
  closePremium: number | null
  closeCommission: number
  closeFees: number
  closeTotal: number | null
  netPosition: number
  returnPct: number
  notes?: string
  positionId?: string
  soldPrice?: number
  sharesSold?: number
  shareAppreciation?: number
  delta?: number | null
  iv?: number | null
  optionBuyPrice?: number | null
  currentOptionPrice?: number | null
}

interface AccountData {
  account: { slug: string; name: string; collateral?: number; totalGains?: number; netProfit?: number; averageReturn?: number }
  trades: Trade[]
}

const TRADE_COLUMNS = [
  { id: 'ticker', label: 'Ticker', sortKey: 'ticker' },
  { id: 'action', label: 'Action', sortKey: 'action' },
  { id: 'strike', label: 'Strike', sortKey: 'strikePrice' },
  { id: 'bought', label: 'Bought', sortKey: 'priceWhenBought' },
  { id: 'current', label: 'Current', sortKey: 'currentPrice' },
  { id: 'optionPrice', label: 'Opt. Price', sortKey: 'currentOptionPrice' },
  { id: 'premium', label: 'Premium', sortKey: 'premium' },
  { id: 'qty', label: 'Qty', sortKey: null },
  { id: 'total', label: 'Total', sortKey: 'total' },
  { id: 'buyDate', label: 'Buy Date', sortKey: 'buyDate' },
  { id: 'expDate', label: 'Exp Date', sortKey: 'expDate' },
  { id: 'days', label: 'Days', sortKey: null },
  { id: 'status', label: 'Status', sortKey: 'status' },
  { id: 'itmOtm', label: 'ITM/OTM', sortKey: null },
  { id: 'closeAction', label: 'Close Action', sortKey: null },
  { id: 'netPnl', label: 'Net P&L', sortKey: 'netPosition' },
  { id: 'return', label: 'Return', sortKey: 'returnPct' },
] as const

export function TradePage() {
  const { accountSlug } = useParams<{ accountSlug: string }>()
  const { setPageTitle } = useMobileNav()
  const [data, setData] = useState<AccountData | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'open' | 'closed'>('open')
  const [searchQuery, setSearchQuery] = useState('')
  const [timePeriod, setTimePeriod] = useState<string>(() => {
    return String(new Date().getFullYear())
  })
  const [tickerFilter, setTickerFilter] = useState<string>('')
  const [openedInMonth, setOpenedInMonth] = useState<string>('')
  const [closedInMonth, setClosedInMonth] = useState<string>('')
  const [showNewForm, setShowNewForm] = useState(false)
  const [newTradeInitialData, setNewTradeInitialData] = useState<any>(undefined)
  const [closingLeap, setClosingLeap] = useState<Trade | null>(null)
  const [closingTrade, setClosingTrade] = useState<Trade | null>(null)
  const [sortCol, setSortCol] = useState<string>('expDate')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [positions, setPositions] = useState<Position[]>([])
  const [showMonthlyPnL, setShowMonthlyPnL] = useState(false)
  const [showPremiumByTicker, setShowPremiumByTicker] = useState(false)
  const [selectedPositionTicker, setSelectedPositionTicker] = useState<string | null>(null)
  const [pnlSortCol, setPnlSortCol] = useState<'month' | 'pnl'>('month')
  const [pnlSortDir, setPnlSortDir] = useState<'asc' | 'desc'>('desc')
  const [pnlYearFilter, setPnlYearFilter] = useState<string>(String(new Date().getFullYear()))
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [syncing, setSyncing] = useState(false)
  const [leapHoldingsCollapsed, setLeapHoldingsCollapsed] = useState(() => {
    try { return localStorage.getItem('tradingApp_leapHoldings_collapsed') === 'true' }
    catch { return false }
  })
  const [leapSortCol, setLeapSortCol] = useState('ticker')
  const [leapSortDir, setLeapSortDir] = useState<'asc' | 'desc'>('asc')

  const handleLeapSort = (col: string) => {
    if (leapSortCol === col) setLeapSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setLeapSortCol(col); setLeapSortDir('asc') }
  }
  const [livePrices, setLivePrices] = useState<Record<string, number>>({})
  const [priceLastUpdated, setPriceLastUpdated] = useState<Date | null>(null)
  const [returnMode, setReturnMode] = useState<'average' | 'annualized'>('average')
  const [showFilterSheet, setShowFilterSheet] = useState(false)

  // Feed the mobile top bar the account name (cleared on unmount).
  useEffect(() => {
    setPageTitle(ACCOUNT_NAMES[accountSlug || ''] || accountSlug || 'Trades', '')
    return () => setPageTitle('')
  }, [accountSlug, setPageTitle])
  const priceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { columnOrder, dragHandlers } = useColumnOrder(
    `trade-${accountSlug}`,
    TRADE_COLUMNS.map(c => c.id)
  )

  const LEAP_COLUMNS = [
    { id: 'ticker', label: 'Ticker' },
    { id: 'strikePrice', label: 'Strike' },
    { id: 'optionBuyPrice', label: 'Option Buy Price' },
    { id: 'currentOptionPrice', label: 'Current Option Price' },
    { id: 'priceWhenBought', label: 'Stock Price (Bought)' },
    { id: 'currentPrice', label: 'Current Stock Price' },
    { id: 'buyDate', label: 'Buy Date' },
    { id: 'expDate', label: 'Exp Date' },
    { id: 'daysLeft', label: 'Days Left' },
    { id: 'total', label: 'Total Cost' },
    { id: 'status', label: 'Status' },
    { id: 'netPosition', label: 'P&L' },
  ] as const

  const { columnOrder: leapColumnOrder, dragHandlers: leapDragHandlers } = useColumnOrder(
    `leap-holdings`,
    LEAP_COLUMNS.map(c => c.id)
  )

  // Fetch live prices for all unique tickers (open AND closed)
  const fetchLivePrices = useCallback(async () => {
    if (!data) return
    const allTickers = Array.from(new Set(
      data.trades.map(t => t.ticker)
    ))
    if (allTickers.length === 0) return

    try {
      await useAppStore.getState().fetchPrices(allTickers)
      const storePrices = useAppStore.getState().prices
      const prices: Record<string, number> = {}
      for (const ticker of allTickers) {
        if (storePrices[ticker]?.currentPrice) {
          prices[ticker] = storePrices[ticker].currentPrice
        }
      }
      setLivePrices(prev => ({ ...prev, ...prices }))
      setPriceLastUpdated(new Date())
    } catch {
      // Silently fail — prices stay at last known value
    }
  }, [data])

  useEffect(() => {
    if (!accountSlug) return
    setLoading(true)

    const loadData = async () => {
      try {
        // Fetch trades from Supabase
        const { data: tradesData, error: tradesError } = await supabase
          .from('trades')
          .select('*')
          .eq('account_slug', accountSlug)

        if (tradesError) throw tradesError

        // Fetch positions from Supabase
        const { data: posData, error: posError } = await supabase
          .from('positions')
          .select('*')
          .eq('account_slug', accountSlug)

        if (posError) throw posError

        // Convert snake_case to camelCase for trades
        const trades: Trade[] = (tradesData || []).map((row: any) => ({
          id: row.id,
          ticker: row.ticker || '',
          action: row.action || row.strategy || '',
          priceWhenBought: row.price_when_bought || 0,
          strikePrice: row.strike_price || 0,
          currentPrice: row.current_price || 0,
          status: row.status || 'open',
          buyDate: row.buy_date || '',
          expDate: row.exp_date || '',
          closingMonth: row.closing_month || '',
          originalDays: row.original_days || 0,
          daysLeft: row.days_left || 0,
          daysKept: row.days_kept || null,
          premium: row.premium || 0,
          qty: row.qty || 0,
          commission: row.commission || 0,
          fees: row.fees || 0,
          total: row.total || 0,
          closeAction: row.close_action || null,
          closeDate: row.close_date || null,
          closePremium: row.close_premium || null,
          closeCommission: row.close_commission || 0,
          closeFees: row.close_fees || 0,
          closeTotal: row.close_total || null,
          netPosition: row.net_position || 0,
          returnPct: row.return_pct || 0,
          notes: row.notes || undefined,
          positionId: row.position_id || undefined,
          soldPrice: row.sold_price || undefined,
          sharesSold: row.shares_sold || undefined,
          shareAppreciation: row.share_appreciation || undefined,
          delta: row.delta ?? null,
          iv: row.iv ?? null,
          optionBuyPrice: row.option_buy_price ?? null,
          currentOptionPrice: row.current_option_price ?? null,
        }))

        // Compute account stats from trades
        const closedTrades = trades.filter(t => t.status !== 'open')
        const totalGains = closedTrades.reduce((sum, t) => sum + (t.netPosition || 0), 0)
        const averageReturn = closedTrades.length > 0
          ? closedTrades.reduce((sum, t) => sum + (t.returnPct || 0), 0) / closedTrades.length
          : 0

        setData({
          account: {
            slug: accountSlug,
            name: ACCOUNT_NAMES[accountSlug] || accountSlug,
            totalGains: Math.round(totalGains * 100) / 100,
            averageReturn: Math.round(averageReturn * 100) / 100,
          },
          trades,
        })

        // Convert positions
        const positions: Position[] = (posData || []).map((row: any) => ({
          id: row.id,
          accountSlug: row.account_slug,
          ticker: row.ticker,
          quantity: row.quantity || row.qty || 0,
          costBasis: row.cost_basis || 0,
          acquisitionDate: row.acquisition_date || row.assigned_date || '',
          sourceTradeId: row.source_trade_id || '',
          status: row.status || 'open',
          closedDate: row.closed_date || undefined,
          soldPrice: row.sold_price || undefined,
          soldDate: row.sold_date || undefined,
          sharesSold: row.shares_sold || undefined,
          notes: row.notes || undefined,
        }))
        setPositions(positions)
      } catch (error) {
        console.error('Failed to load trade data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [accountSlug])

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set())
  }, [filter, searchQuery, timePeriod])

  const fetchEarningsCalendar = useAppStore(s => s.fetchEarningsCalendar)

  // Live price polling: fetch on load, every 60s, and on tab focus
  useEffect(() => {
    fetchLivePrices()
  }, [fetchLivePrices])

  // Fetch earnings dates for all tickers on this page (auto-populates from Finnhub if stale/missing)
  useEffect(() => {
    if (!data) return
    const allTickers = [...new Set([
      ...data.trades.map(t => t.ticker),
      ...positions.map(p => p.ticker),
    ])].filter(Boolean)
    if (allTickers.length > 0) {
      fetchEarningsCalendar(allTickers)
    }
  }, [data, positions, fetchEarningsCalendar])

  // Fetch current option ASK prices from Schwab for all open trades
  useEffect(() => {
    if (!data) return
    const openTrades = data.trades.filter(t => t.status === 'open' && t.strikePrice && t.expDate)
    if (openTrades.length === 0) return

    const fetchOptionPrices = async () => {
      try {
        const trades = openTrades.map(t => ({
          tradeId: t.id,
          ticker: t.ticker,
          strike: t.strikePrice,
          expDate: t.expDate,
          action: t.action,
        }))

        const result = await invokeEdgeFunction<{ results: Array<{ tradeId: string; askPrice: number | null }> }>('option-prices', {
          method: 'POST',
          body: { trades } as Record<string, unknown>,
        })

        if (result.results && result.results.length > 0) {
          setData(d => {
            if (!d) return d
            return { ...d, trades: d.trades.map(t => {
              const match = result.results.find(r => r.tradeId === t.id)
              if (match && match.askPrice !== null) {
                return { ...t, currentOptionPrice: match.askPrice }
              }
              return t
            })}
          })
        }
      } catch (err) {
        console.warn('Option price fetch failed:', err)
      }
    }

    fetchOptionPrices()
  }, [data?.trades.filter(t => t.status === 'open').length])

  useEffect(() => {
    priceIntervalRef.current = setInterval(fetchLivePrices, 60_000)
    return () => { if (priceIntervalRef.current) clearInterval(priceIntervalRef.current) }
  }, [fetchLivePrices])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchLivePrices()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [fetchLivePrices])

  if (loading) return <div className="p-6 text-[var(--color-text-muted)]">Loading trades...</div>
  if (!data) return <div className="p-6 text-[var(--color-text-muted)]">No data found for this account.</div>

  const handleNewTrade = async (trade: any) => {
    try {
      const { data: newRow, error } = await supabase.from('trades').insert({
        account_slug: accountSlug,
        ticker: trade.ticker?.toUpperCase() || trade.ticker,
        action: trade.action,
        strategy: trade.action,
        price_when_bought: trade.priceWhenBought || 0,
        strike_price: trade.strikePrice || 0,
        current_price: trade.currentPrice || 0,
        status: trade.status || 'open',
        buy_date: trade.buyDate || null,
        exp_date: trade.expDate || null,
        closing_month: trade.closingMonth || null,
        original_days: trade.originalDays || 0,
        days_left: trade.daysLeft || 0,
        premium: trade.premium || 0,
        qty: trade.qty || 0,
        commission: trade.commission || 0,
        fees: trade.fees || 0,
        total: trade.total || 0,
        net_position: trade.netPosition || trade.total || 0,
        return_pct: trade.returnPct || 0,
        notes: trade.notes || null,
        position_id: trade.positionId || null,
        delta: trade.delta || null,
        iv: trade.iv || null,
        user_id: USER_ID,
      }).select().single()

      if (error) throw error

      const newTrade: Trade = {
        id: newRow.id,
        ticker: newRow.ticker || '',
        action: newRow.action || newRow.strategy || '',
        priceWhenBought: newRow.price_when_bought || 0,
        strikePrice: newRow.strike_price || 0,
        currentPrice: newRow.current_price || 0,
        status: newRow.status || 'open',
        buyDate: newRow.buy_date || '',
        expDate: newRow.exp_date || '',
        closingMonth: newRow.closing_month || '',
        originalDays: newRow.original_days || 0,
        daysLeft: newRow.days_left || 0,
        daysKept: newRow.days_kept || null,
        premium: newRow.premium || 0,
        qty: newRow.qty || 0,
        commission: newRow.commission || 0,
        fees: newRow.fees || 0,
        total: newRow.total || 0,
        closeAction: newRow.close_action || null,
        closeDate: newRow.close_date || null,
        closePremium: newRow.close_premium || null,
        closeTotal: newRow.close_total || null,
        netPosition: newRow.net_position || 0,
        returnPct: newRow.return_pct || 0,
        notes: newRow.notes || undefined,
        positionId: newRow.position_id || undefined,
        delta: newRow.delta ?? null,
        iv: newRow.iv ?? null,
      }
      setData(d => d ? { ...d, trades: [...d.trades, newTrade] } : d)
      setShowNewForm(false)
      setNewTradeInitialData(undefined)
    } catch (error) {
      console.error('Failed to create trade:', error)
    }
  }

  const handleCloseTrade = async (updatedTrade: any) => {
    // Update local state immediately
    setData(d => d ? { ...d, trades: d.trades.map(t => t.id === updatedTrade.id ? updatedTrade : t) } : d)
    // Persist to Supabase
    try {
      const { error } = await supabase.from('trades').update({
        status: updatedTrade.status || 'closed',
        close_action: updatedTrade.closeAction || null,
        close_date: updatedTrade.closeDate || null,
        close_premium: updatedTrade.closePremium || null,
        close_commission: updatedTrade.closeCommission || 0,
        close_fees: updatedTrade.closeFees || 0,
        close_total: updatedTrade.closeTotal || null,
        net_position: updatedTrade.netPosition || 0,
        return_pct: updatedTrade.returnPct || 0,
        days_kept: updatedTrade.daysKept || null,
        current_price: updatedTrade.currentPrice || 0,
      }).eq('id', updatedTrade.id)

      if (error) console.error('Failed to close trade:', error)
    } catch (error) {
      console.error('Failed to close trade:', error)
    }
    setClosingTrade(null)

    // If rolled, auto-open NewTradeForm pre-filled with the rolled trade's details
    if (updatedTrade.closeAction === 'Rolled') {
      setNewTradeInitialData({
        ticker: updatedTrade.ticker,
        action: updatedTrade.action,
        qty: String(updatedTrade.qty || 1),
        buyDate: updatedTrade.closeDate || new Date().toISOString().split('T')[0],
      })
      setShowNewForm(true)
    }
  }

  const handleCloseLeap = async (leap: Trade, sellPremium: number, closeCommission: number, closeFees: number, closeDate: string) => {
    // LEAP is a Buy (debit). Closing = selling.
    // Net P&L = (sellPremium × 100 × qty) - originalCost - |commission| - |fees|
    // originalCost is stored as negative in `total`, so we add it back.
    const qty = Number(leap.qty) || 0
    const closeTotal = (sellPremium * 100 * qty) - Math.abs(closeCommission) - Math.abs(closeFees)
    const netPosition = closeTotal + (Number(leap.total) || 0) // total is negative (the debit paid)

    const buyDate = new Date(leap.buyDate)
    const cd = new Date(closeDate)
    const daysKept = Math.ceil((cd.getTime() - buyDate.getTime()) / (1000 * 60 * 60 * 24))
    const returnPct = leap.total !== 0 ? (netPosition / Math.abs(leap.total)) * 100 : 0

    const updated: Trade = {
      ...leap,
      status: 'closed',
      closeAction: 'Sold',
      closeDate,
      closePremium: sellPremium,
      closeCommission: -Math.abs(closeCommission),
      closeFees: -Math.abs(closeFees),
      closeTotal: Math.round(closeTotal * 100) / 100,
      netPosition: Math.round(netPosition * 100) / 100,
      daysKept,
      returnPct: Math.round(returnPct * 100) / 100,
    }

    setData(d => d ? { ...d, trades: d.trades.map(t => t.id === leap.id ? updated : t) } : d)

    const { error } = await supabase.from('trades').update({
      status: 'closed',
      close_action: 'Sold',
      close_date: closeDate,
      close_premium: sellPremium,
      close_commission: -Math.abs(closeCommission),
      close_fees: -Math.abs(closeFees),
      close_total: updated.closeTotal,
      net_position: updated.netPosition,
      days_kept: daysKept,
      return_pct: updated.returnPct,
    }).eq('id', leap.id)
    if (error) console.error('Failed to close LEAP:', error)
    setClosingLeap(null)
  }

  const handleDelete = async (tradeId: string) => {
    if (!confirm('Delete this trade?')) return
    const { error } = await supabase.from('trades').delete().eq('id', tradeId)
    if (!error) {
      setData(d => d ? { ...d, trades: d.trades.filter(t => t.id !== tradeId) } : d)
    } else {
      console.error('Failed to delete trade:', error)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Delete ${selectedIds.size} selected trade${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return
    const idsToDelete = Array.from(selectedIds)
    const { error } = await supabase.from('trades').delete().in('id', idsToDelete)
    if (!error) {
      setData(d => d ? { ...d, trades: d.trades.filter(t => !selectedIds.has(t.id)) } : d)
      setSelectedIds(new Set())
    } else {
      console.error('Failed to bulk delete trades:', error)
    }
  }

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('desc') }
  }

  const handleSync = async () => {
    // Sync is disabled — no-op
    console.log('Google Sheet sync is disabled in v2')
  }

  const handleInlineEdit = async (tradeId: string, field: string, newValue: string | number) => {
    // Update local state immediately, recomputing totals if related fields change
    setData(d => {
      if (!d) return d
      return { ...d, trades: d.trades.map(t => {
        if (t.id !== tradeId) return t
        const updated = { ...t, [field]: newValue }

        // Recompute total when premium, qty, commission, or fees change
        // Formula: total = (premium × 100 × qty) + commission + fees
        // (commission and fees are stored as negative for debits)
        const totalFields = ['premium', 'qty', 'commission', 'fees']
        if (totalFields.includes(field)) {
          const premium = Number(field === 'premium' ? newValue : updated.premium) || 0
          const qty = Number(field === 'qty' ? newValue : updated.qty) || 0
          const commission = -Math.abs(Number(field === 'commission' ? newValue : updated.commission) || 0)
          const fees = -Math.abs(Number(field === 'fees' ? newValue : updated.fees) || 0)
          updated.total = (premium * 100 * qty) + commission + fees
        }

        // Recompute closeTotal when closePremium changes
        // Skip for "Sold" trades — closePremium stores sold price/share, not option premium
        const closeFields = ['closePremium', 'closeCommission', 'closeFees']
        if (closeFields.includes(field) && updated.closeAction !== 'Sold') {
          const closePremium = Number(field === 'closePremium' ? newValue : updated.closePremium) || 0
          const qty = Number(updated.qty) || 0
          const closeCommission = Number(field === 'closeCommission' ? newValue : updated.closeCommission) || 0
          const closeFees = Number(field === 'closeFees' ? newValue : updated.closeFees) || 0
          updated.closeTotal = -((closePremium * 100 * qty) + Math.abs(closeCommission) + Math.abs(closeFees))
        }

        // Recompute netPosition when total or closeTotal change
        if (totalFields.includes(field) || closeFields.includes(field) || field === 'total' || field === 'closeTotal') {
          const total = Number(updated.total) || 0
          const closeTotal = Number(updated.closeTotal) || 0
          updated.netPosition = total + closeTotal
        }

        return updated
      })}
    })
    // Persist to API — send the recomputed trade
    const updatedData = data.trades.find(t => t.id === tradeId)
    if (!updatedData) return
    const updatedTrade = { ...updatedData, [field]: newValue }

    // Recompute for persistence too
    const totalFields = ['premium', 'qty', 'commission', 'fees']
    if (totalFields.includes(field)) {
      const premium = Number(field === 'premium' ? newValue : updatedTrade.premium) || 0
      const qty = Number(field === 'qty' ? newValue : updatedTrade.qty) || 0
      const commission = -Math.abs(Number(field === 'commission' ? newValue : updatedTrade.commission) || 0)
      const fees = -Math.abs(Number(field === 'fees' ? newValue : updatedTrade.fees) || 0)
      updatedTrade.total = (premium * 100 * qty) + commission + fees
      updatedTrade.netPosition = (updatedTrade.total || 0) + (Number(updatedTrade.closeTotal) || 0)
    }
    const closeFields = ['closePremium', 'closeCommission', 'closeFees']
    if (closeFields.includes(field) && updatedTrade.closeAction !== 'Sold') {
      const closePremium = Number(field === 'closePremium' ? newValue : updatedTrade.closePremium) || 0
      const qty = Number(updatedTrade.qty) || 0
      const closeCommission = Number(field === 'closeCommission' ? newValue : updatedTrade.closeCommission) || 0
      const closeFees = Number(field === 'closeFees' ? newValue : updatedTrade.closeFees) || 0
      updatedTrade.closeTotal = -((closePremium * 100 * qty) + Math.abs(closeCommission) + Math.abs(closeFees))
      updatedTrade.netPosition = (Number(updatedTrade.total) || 0) + updatedTrade.closeTotal
    }

    const res = await supabase.from('trades').update({
      [field.replace(/([A-Z])/g, '_$1').toLowerCase()]: newValue,
      ...(totalFields.includes(field) ? { total: updatedTrade.total, net_position: updatedTrade.netPosition } : {}),
      ...(closeFields.includes(field) ? { close_total: updatedTrade.closeTotal, net_position: updatedTrade.netPosition } : {}),
    }).eq('id', tradeId)
  }

  const handleRecalculate = async (tradeId: string) => {
    const trade = data.trades.find(t => t.id === tradeId)
    if (!trade) return

    // Recompute total (open side): premium × 100 × qty + commission + fees
    const premium = Number(trade.premium) || 0
    const qty = Number(trade.qty) || 0
    const commission = -Math.abs(Number(trade.commission) || 0)
    const fees = -Math.abs(Number(trade.fees) || 0)
    const total = (premium * 100 * qty) + commission + fees

    // Recompute closeTotal (close side): -(closePremium × 100 × qty + abs(closeCommission) + abs(closeFees))
    let closeTotal = Number(trade.closeTotal) || 0
    if (trade.closeAction === 'Buy Back') {
      const closePremium = Number(trade.closePremium) || 0
      const closeCommission = Number(trade.closeCommission) || 0
      const closeFees = Number(trade.closeFees) || 0
      closeTotal = -((closePremium * 100 * qty) + Math.abs(closeCommission) + Math.abs(closeFees))
    } else if (trade.closeAction === 'Expired Worthless') {
      closeTotal = 0
    } else if (trade.closeAction === 'Assigned') {
      closeTotal = 0
    }

    // Recompute netPosition and returnPct
    const netPosition = total + closeTotal
    const returnPct = total !== 0 ? (netPosition / Math.abs(total)) * 100 : 0

    // Update local state
    setData(d => {
      if (!d) return d
      return { ...d, trades: d.trades.map(t => {
        if (t.id !== tradeId) return t
        return { ...t, total, closeTotal, netPosition, returnPct }
      })}
    })

    // Persist to Supabase
    await supabase.from('trades').update({
      total,
      close_total: closeTotal,
      net_position: netPosition,
      return_pct: returnPct,
    }).eq('id', tradeId)
  }

  const filteredTrades = data.trades.filter(t => {
    // For leap-calls, hide OPEN Buy entries from main table (shown in LEAP Holdings section).
    // Closed LEAP buys DO appear in the main trades table below.
    if (accountSlug === 'leap-calls' && t.action === 'Buy' && t.status === 'open') return false

    // Position ticker filter
    if (selectedPositionTicker && t.ticker !== selectedPositionTicker) return false

    // Ticker dropdown filter
    if (tickerFilter && t.ticker !== tickerFilter) return false

    // Status filter
    if (filter === 'open' && t.status !== 'open') return false
    if (filter === 'closed' && t.status === 'open') return false

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      if (!t.ticker.toLowerCase().includes(q) && !t.action.toLowerCase().includes(q)) return false
    }

    // Time period filter (using shared utility)
    if (timePeriod !== 'all') {
      const filtered = filterTradesByPeriod([t], timePeriod)
      if (filtered.length === 0) return false
    }

    // Opened in month filter
    if (openedInMonth && !t.buyDate.startsWith(openedInMonth)) return false

    // Closed in month filter
    if (closedInMonth && !t.closeDate?.startsWith(closedInMonth)) return false

    return true
  })

  const openTrades = filteredTrades.filter(t => t.status === 'open')
  const closedTrades = filteredTrades.filter(t => t.status !== 'open')
  const totalPnL = closedTrades.reduce((sum, t) => sum + (t.netPosition || 0), 0)
  const openPremium = openTrades.reduce((sum, t) => sum + (t.total || 0), 0)

  // Sort filtered trades
  const sortedTrades = [...filteredTrades].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    // Custom status sort: open before closed in ascending
    if (sortCol === 'status') {
      const priority: Record<string, number> = { open: 0, closed: 1 }
      return ((priority[a.status] ?? 2) - (priority[b.status] ?? 2)) * dir
    }
    const av = (a as any)[sortCol]
    const bv = (b as any)[sortCol]
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir
    return ((av || 0) - (bv || 0)) * dir
  })

  // For LEAP Calls page, LEAP Holdings shows only OPEN Buy entries.
  // Closed LEAP buys move to the main trades table below.
  const leapBuys = accountSlug === 'leap-calls' ? data.trades.filter(t => t.action === 'Buy' && t.status === 'open') : []
  const displayTrades = sortedTrades

  return (
    <div className="h-full flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[var(--color-border)] px-4 lg:px-6 pt-3 lg:pt-4 pb-3">
        {/* Mobile compact summary strip (shown < lg) */}
        <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar mb-3">
          <div className="shrink-0 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 min-w-[104px]">
            <div className="text-[10px] uppercase text-[var(--color-text-muted)]">Net P&L</div>
            <div className={`text-base font-bold ${totalPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>${totalPnL.toFixed(2)}</div>
          </div>
          <div className="shrink-0 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 min-w-[104px]">
            <div className="text-[10px] uppercase text-[var(--color-text-muted)]">Open Prem</div>
            <div className="text-base font-bold text-[var(--color-accent)]">${openPremium.toFixed(2)}</div>
          </div>
          <div className="shrink-0 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 min-w-[104px]">
            <div className="text-[10px] uppercase text-[var(--color-text-muted)]">Avg Return</div>
            <div className="text-base font-bold text-[var(--color-text-primary)]">{data.account.averageReturn || 0}%</div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="hidden lg:block">
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
              {ACCOUNT_NAMES[accountSlug || ''] || accountSlug}
            </h1>
            {data.account.collateral && (
              <p className="text-sm text-[var(--color-text-muted)]">
                Collateral: ${data.account.collateral?.toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 lg:gap-4 overflow-x-auto no-scrollbar flex-1 lg:flex-none">
            <button
              onClick={handleSync}
              disabled={true}
              title="Sync temporarily disabled — under investigation"
              className="shrink-0 whitespace-nowrap px-3 lg:px-5 py-2 lg:py-2.5 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ height: '44px' }}
            >
              {syncing ? '⏳ Syncing...' : '🔄 Sync'}
            </button>
            <button
              onClick={() => setShowMonthlyPnL(true)}
              className="shrink-0 whitespace-nowrap px-3 lg:px-5 py-2 lg:py-2.5 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
              style={{ height: '44px' }}
            >
              📊 Monthly P&L
            </button>
            <button
              onClick={() => setShowPremiumByTicker(true)}
              className="shrink-0 whitespace-nowrap px-3 lg:px-5 py-2 lg:py-2.5 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
              style={{ height: '44px' }}
            >
              💰 By Ticker
            </button>
            <button
              onClick={() => { setNewTradeInitialData(undefined); setShowNewForm(true) }}
              className="shrink-0 whitespace-nowrap px-3 lg:px-5 py-2 lg:py-2.5 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80"
              style={{ height: '44px' }}
            >
              + New Trade
            </button>
            {/* Desktop-only stat blocks (mobile uses the summary strip above) */}
            <div className="hidden lg:block text-right">
              <p className="text-sm text-[var(--color-text-muted)]">Net P&L</p>
              <p className={`text-lg font-bold ${totalPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                ${totalPnL.toFixed(2)}
              </p>
            </div>
            <div className="hidden lg:block text-right">
              <p className="text-sm text-[var(--color-text-muted)]">Open Premium</p>
              <p className="text-lg font-bold text-[var(--color-accent)]">${openPremium.toFixed(2)}</p>
            </div>
            <div className="hidden lg:block text-right">
              <p className="text-sm text-[var(--color-text-muted)]">Avg Return</p>
              <p className="text-lg font-bold text-[var(--color-text-primary)]">{data.account.averageReturn || 0}%</p>
            </div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-3">
          {(['all', 'open', 'closed'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filter === f
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'
              }`}
            >
              {f === 'all' ? `All (${data.trades.length})` : f === 'open' ? `Open (${openTrades.length})` : `Closed (${closedTrades.length})`}
            </button>
          ))}
        </div>

        {/* Search + Time Period + Ticker Filter + Month Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
              placeholder="🔍 Search by ticker (e.g. NVDA)..."
              className="w-full px-4 py-2 pr-9 rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent)]"
              style={{ height: '44px' }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
          {/* Mobile Filters trigger (opens bottom sheet) — shown < lg */}
          <button
            onClick={() => setShowFilterSheet(true)}
            className={`lg:hidden shrink-0 px-4 rounded-lg border text-sm font-medium ${
              (tickerFilter || openedInMonth || closedInMonth)
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                : 'border-[var(--color-border)] bg-white text-[var(--color-text-secondary)]'
            }`}
            style={{ height: '44px' }}
          >
            ⚙ Filters{(tickerFilter || openedInMonth || closedInMonth) ? ' •' : ''}
          </button>
          {/* Desktop filter selects — shown lg+ */}
          <select
            value={tickerFilter}
            onChange={(e) => setTickerFilter(e.target.value)}
            className={`hidden lg:block px-4 py-2 rounded-lg border transition-colors ${
              tickerFilter
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)] font-medium'
                : 'border-[var(--color-border)] bg-white text-[var(--color-text-secondary)]'
            }`}
            style={{ height: '44px' }}
          >
            <option value="">All Tickers</option>
            {Array.from(new Set(data.trades.map(t => t.ticker))).sort().map(ticker => (
              <option key={ticker} value={ticker}>{ticker}</option>
            ))}
          </select>
          <select
            value={openedInMonth}
            onChange={(e) => setOpenedInMonth(e.target.value)}
            className={`hidden lg:block px-4 py-2 rounded-lg border transition-colors ${
              openedInMonth
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)] font-medium'
                : 'border-[var(--color-border)] bg-white text-[var(--color-text-secondary)]'
            }`}
            style={{ height: '44px' }}
          >
            <option value="">Opened in: All</option>
            {Array.from(new Set(data.trades.map(t => t.buyDate?.slice(0, 7)).filter(Boolean))).sort().map(ym => {
              const [y, m] = ym!.split('-')
              const label = new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
              return <option key={ym} value={ym}>{label}</option>
            })}
          </select>
          <select
            value={closedInMonth}
            onChange={(e) => setClosedInMonth(e.target.value)}
            className={`hidden lg:block px-4 py-2 rounded-lg border transition-colors ${
              closedInMonth
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)] font-medium'
                : 'border-[var(--color-border)] bg-white text-[var(--color-text-secondary)]'
            }`}
            style={{ height: '44px' }}
          >
            <option value="">Closed in: All</option>
            {Array.from(new Set(data.trades.filter(t => t.closeDate).map(t => t.closeDate!.slice(0, 7)))).sort().map(ym => {
              const [y, m] = ym.split('-')
              const label = new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
              return <option key={ym} value={ym}>{label}</option>
            })}
          </select>
          <TimePeriodFilter value={timePeriod} onChange={setTimePeriod} />
        </div>
      </div>

      {/* Trade table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {/* Account Cash Controls */}
        <CashControlsSection accountSlug={accountSlug || ''} />

        {/* LEAP Holdings Section — only on leap-calls page */}
        {accountSlug === 'leap-calls' && leapBuys.length > 0 && (
          <div className="mb-6 border border-[var(--color-border)] rounded-lg overflow-hidden">
            <div
              className="flex items-center gap-2 px-4 py-3 bg-[var(--color-bg-tertiary)] cursor-pointer select-none"
              onClick={() => {
                const next = !leapHoldingsCollapsed
                setLeapHoldingsCollapsed(next)
                localStorage.setItem('tradingApp_leapHoldings_collapsed', String(next))
              }}
            >
              <span className={`text-xs text-[var(--color-text-muted)] transition-transform ${leapHoldingsCollapsed ? '' : 'rotate-90'}`}>&#9654;</span>
              <h2 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">
                LEAP Holdings ({leapBuys.length})
              </h2>
            </div>
            {!leapHoldingsCollapsed && (
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-bg-tertiary)]">
                  <tr>
                    {leapColumnOrder.map((colId, index) => {
                      const col = LEAP_COLUMNS.find(c => c.id === colId)
                      if (!col) return null
                      return (
                        <th
                          key={col.id}
                          className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase cursor-pointer hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] select-none whitespace-nowrap"
                          onClick={() => handleLeapSort(col.id)}
                          {...leapDragHandlers(index)}
                        >
                          {col.label} {leapSortCol === col.id && (leapSortDir === 'asc' ? '↑' : '↓')}
                        </th>
                      )
                    })}
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...leapBuys].sort((a: any, b: any) => {
                    const dir = leapSortDir === 'asc' ? 1 : -1
                    const valA = a[leapSortCol]
                    const valB = b[leapSortCol]
                    if (leapSortCol === 'daysLeft') {
                      const dA = a.expDate ? Math.round((new Date(a.expDate).getTime() - Date.now()) / 86400000) : 0
                      const dB = b.expDate ? Math.round((new Date(b.expDate).getTime() - Date.now()) / 86400000) : 0
                      return (dA - dB) * dir
                    }
                    if (typeof valA === 'number' && typeof valB === 'number') return (valA - valB) * dir
                    return String(valA || '').localeCompare(String(valB || '')) * dir
                  }).map(leap => {
                    const daysLeft = leap.expDate ? (() => {
                      const [y, m, d] = leap.expDate.split('-').map(Number)
                      const exp = new Date(y, m - 1, d)
                      const today = new Date()
                      today.setHours(0, 0, 0, 0)
                      return Math.round((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                    })() : 0

                    const renderLeapCell = (colId: string) => {
                      switch (colId) {
                        case 'ticker':
                          return <td key={colId} className="px-3 py-2 font-medium text-[var(--color-accent)]">{leap.ticker}<EarningsBadge ticker={leap.ticker} /></td>
                        case 'strikePrice':
                          return <td key={colId} className="px-3 py-2 font-mono">${leap.strikePrice}</td>
                        case 'optionBuyPrice':
                          return (
                            <td key={colId} className="px-3 py-2 font-mono" onClick={e => e.stopPropagation()}>
                              <InlineEdit value={leap.optionBuyPrice ?? ''} fieldName="optionBuyPrice" type="number" onSave={(v) => handleInlineEdit(leap.id, 'optionBuyPrice', v)} displayFormatter={(v) => v ? `$${Number(v).toFixed(2)}` : '—'} className="font-mono" />
                            </td>
                          )
                        case 'currentOptionPrice':
                          return (
                            <td key={colId} className="px-3 py-2 font-mono" onClick={e => e.stopPropagation()}>
                              <InlineEdit value={leap.currentOptionPrice ?? ''} fieldName="currentOptionPrice" type="number" onSave={(v) => handleInlineEdit(leap.id, 'currentOptionPrice', v)} displayFormatter={(v) => v ? `$${Number(v).toFixed(2)}` : '—'} className="font-mono" />
                            </td>
                          )
                        case 'priceWhenBought':
                          return <td key={colId} className="px-3 py-2 font-mono text-[var(--color-text-secondary)]">{leap.priceWhenBought ? `$${leap.priceWhenBought.toFixed(2)}` : '—'}</td>
                        case 'currentPrice':
                          return <td key={colId} className="px-3 py-2 font-mono text-[var(--color-text-secondary)]">{(() => {
                            const livePrice = leap.status === 'open' ? livePrices[leap.ticker] : null
                            const displayPrice = livePrice || leap.currentPrice
                            return displayPrice ? `$${displayPrice.toFixed(2)}` : '—'
                          })()}</td>
                        case 'buyDate':
                          return <td key={colId} className="px-3 py-2 text-[var(--color-text-secondary)]">{formatDateShort(leap.buyDate)}</td>
                        case 'expDate':
                          return <td key={colId} className="px-3 py-2 text-[var(--color-text-secondary)]">{formatDateShort(leap.expDate)}</td>
                        case 'daysLeft':
                          return <td key={colId} className="px-3 py-2">{leap.status === 'open' ? `${daysLeft}d` : '—'}</td>
                        case 'total':
                          return <td key={colId} className="px-3 py-2 font-mono">${Math.abs(leap.total ?? 0).toFixed(2)}</td>
                        case 'status':
                          return (
                            <td key={colId} className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                leap.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                              }`}>{leap.status}</span>
                            </td>
                          )
                        case 'netPosition':
                          return (
                            <td key={colId} className={`px-3 py-2 font-mono font-medium ${
                              leap.netPosition > 0 ? 'text-[var(--color-green)]' : leap.netPosition < 0 ? 'text-[var(--color-red)]' : 'text-[var(--color-text-muted)]'
                            }`}>
                              {leap.netPosition ? `$${leap.netPosition.toFixed(2)}` : '—'}
                            </td>
                          )
                        default:
                          return <td key={colId} className="px-3 py-2"></td>
                      }
                    }

                    return (
                      <React.Fragment key={leap.id}>
                      <tr
                        className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]/50 cursor-pointer group ${
                          expandedId === leap.id ? 'bg-[var(--color-bg-tertiary)]' : ''
                        } ${selectedPositionTicker === leap.ticker ? 'bg-[var(--color-accent)]/5 border-l-4 border-l-[var(--color-accent)]' : ''}`}
                        onClick={() => setExpandedId(expandedId === leap.id ? null : leap.id)}
                      >
                        {leapColumnOrder.map(colId => renderLeapCell(colId))}
                        <td className="px-2 py-2 whitespace-nowrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); setClosingLeap(leap) }}
                            className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200 opacity-0 group-hover:opacity-100 transition-opacity mr-1"
                            title="Close LEAP"
                          >Close</button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation()
                              if (!confirm(`Delete ${leap.ticker} LEAP position?`)) return
                              const { error } = await supabase.from('trades').delete().eq('id', leap.id)
                              if (!error) {
                                setData(d => d ? { ...d, trades: d.trades.filter(t => t.id !== leap.id) } : d)
                              }
                            }}
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-red)] opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete"
                          >🗑️</button>
                        </td>
                      </tr>
                      {/* Expandable detail row for LEAP Holdings */}
                      {expandedId === leap.id && (
                        <tr className="bg-[var(--color-bg-tertiary)]/70">
                          <td colSpan={leapColumnOrder.length + 1} className="px-6 py-4" onClick={e => e.stopPropagation()}>
                            <div className="grid grid-cols-3 gap-x-8">
                              <div>
                                <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-2">Open Details</h4>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Price When Bought:</span><span className="font-mono">
                                    <InlineEdit value={leap.priceWhenBought ?? ''} fieldName="priceWhenBought" onSave={(v) => handleInlineEdit(leap.id, 'priceWhenBought', v)} type="number" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Premium:</span><span className="font-mono">
                                    <InlineEdit value={leap.premium ?? ''} fieldName="premium" onSave={(v) => handleInlineEdit(leap.id, 'premium', v)} type="number" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Qty:</span><span className="font-mono">
                                    <InlineEdit value={leap.qty ?? ''} fieldName="qty" onSave={(v) => handleInlineEdit(leap.id, 'qty', v)} type="number" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Commission:</span><span className="font-mono">
                                    <InlineEdit value={leap.commission ?? ''} fieldName="commission" onSave={(v) => handleInlineEdit(leap.id, 'commission', v)} type="number" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Fees:</span><span className="font-mono">
                                    <InlineEdit value={leap.fees ?? ''} fieldName="fees" onSave={(v) => handleInlineEdit(leap.id, 'fees', v)} type="number" />
                                  </span></div>
                                  <div className="flex justify-between font-medium"><span className="text-[var(--color-text-muted)]">Total Cost:</span><span className="font-mono text-[var(--color-red)]">${(leap.total ?? 0).toFixed(2)}</span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Strike:</span><span className="font-mono">
                                    <InlineEdit value={leap.strikePrice ?? ''} fieldName="strikePrice" onSave={(v) => handleInlineEdit(leap.id, 'strikePrice', v)} type="number" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Buy Date:</span><span>
                                    <InlineEdit value={leap.buyDate ?? ''} fieldName="buyDate" onSave={(v) => handleInlineEdit(leap.id, 'buyDate', v)} type="date" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Exp Date:</span><span>
                                    <InlineEdit value={leap.expDate ?? ''} fieldName="expDate" onSave={(v) => handleInlineEdit(leap.id, 'expDate', v)} type="date" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Delta:</span><span className="font-mono">
                                    <InlineEdit value={leap.delta ?? ''} fieldName="delta" onSave={(v) => handleInlineEdit(leap.id, 'delta', v)} type="number" />
                                  </span></div>
                                  <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">IV:</span><span className="font-mono">
                                    <InlineEdit value={leap.iv ?? ''} fieldName="iv" onSave={(v) => handleInlineEdit(leap.id, 'iv', v)} type="number" />
                                  </span></div>
                                </div>
                              </div>
                              {leap.status !== 'open' ? (
                                <div>
                                  <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-2">Close Details</h4>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Close Action:</span><span className="font-medium">
                                      <InlineEdit value={leap.closeAction ?? ''} fieldName="closeAction" onSave={(v) => handleInlineEdit(leap.id, 'closeAction', v)} type="text" />
                                    </span></div>
                                    <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Close Date:</span><span>
                                      <InlineEdit value={leap.closeDate ?? ''} fieldName="closeDate" onSave={(v) => handleInlineEdit(leap.id, 'closeDate', v)} type="date" />
                                    </span></div>
                                    <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Sell Premium:</span><span className="font-mono">
                                      <InlineEdit value={leap.closePremium ?? ''} fieldName="closePremium" onSave={(v) => handleInlineEdit(leap.id, 'closePremium', v)} type="number" />
                                    </span></div>
                                    <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Close Total:</span><span className="font-mono">
                                      <InlineEdit value={leap.closeTotal ?? ''} fieldName="closeTotal" onSave={(v) => handleInlineEdit(leap.id, 'closeTotal', v)} type="number" />
                                    </span></div>
                                    <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Days Kept:</span><span>
                                      <InlineEdit value={leap.daysKept ?? ''} fieldName="daysKept" onSave={(v) => handleInlineEdit(leap.id, 'daysKept', v)} type="number" />
                                    </span></div>
                                    <div className="flex justify-between font-medium border-t border-[var(--color-border)] pt-1 mt-1">
                                      <span className="text-[var(--color-text-muted)]">Net P&L:</span>
                                      <span className={`font-mono ${(leap.netPosition ?? 0) > 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                                        ${(leap.netPosition ?? 0).toFixed(2)} ({(leap.returnPct ?? 0).toFixed(2)}%)
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ) : <div />}
                              <div>
                                <label className="text-xs font-semibold text-[var(--color-text-muted)] uppercase">Notes</label>
                                <textarea
                                  defaultValue={leap.notes || ''}
                                  placeholder="Add notes..."
                                  rows={4}
                                  className="mt-1 w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)] resize-none focus:border-[var(--color-accent)] focus:outline-none"
                                  onClick={(e) => e.stopPropagation()}
                                  onBlur={async (e) => {
                                    const newNotes = e.target.value
                                    if (newNotes !== (leap.notes || '')) {
                                      await handleInlineEdit(leap.id, 'notes', newNotes)
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Positions Section */}
        <PositionsSection
          positions={positions}
          accountSlug={accountSlug || ''}
          trades={data.trades}
          prices={{}}
          onSelectPosition={(ticker) => setSelectedPositionTicker(ticker)}
          onAddPosition={async (pos) => {
            // NOTE: the positions table columns are account_slug, ticker, quantity, cost_basis,
            // assigned_date, status, linked_trade_id, user_id (see migration 001). The old code
            // sent non-existent columns (acquisition_date/source_trade_id/notes), so PostgREST
            // rejected the whole insert and the error was silently swallowed. Map to real columns.
            const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
            const insertRow: Record<string, unknown> = {
              account_slug: accountSlug || null,
              ticker: pos.ticker,
              quantity: pos.quantity,
              cost_basis: pos.costBasis,
              assigned_date: pos.acquisitionDate || null,
              status: pos.status || 'open',
              user_id: USER_ID,
            }
            if (pos.sourceTradeId && UUID_RE.test(pos.sourceTradeId)) {
              insertRow.linked_trade_id = pos.sourceTradeId
            }
            // No .select().single() — avoid the documented PGRST116 false-fail; derive the row
            // client-side and re-fetch from DB to stay authoritative.
            const { error } = await supabase.from('positions').insert(insertRow)
            if (error) {
              console.error('Failed to add position:', error)
              alert(`Failed to add position: ${error.message}`)
              return
            }
            // Re-fetch positions from Supabase so the UI reflects the DB source of truth.
            const { data: rows, error: fetchErr } = await supabase
              .from('positions')
              .select('*')
              .eq('account_slug', accountSlug)
            if (!fetchErr && rows) {
              setPositions(
                rows.map((r: any) => ({
                  id: r.id,
                  accountSlug: r.account_slug,
                  ticker: r.ticker,
                  quantity: r.quantity || 0,
                  costBasis: r.cost_basis || 0,
                  acquisitionDate: r.assigned_date || '',
                  sourceTradeId: r.linked_trade_id || '',
                  status: r.status || 'open',
                  closedDate: r.sold_date || undefined,
                  soldPrice: r.sold_price ?? undefined,
                  sharesSold: r.shares_sold ?? undefined,
                  notes: undefined,
                }))
              )
            }
          }}
          onUpdatePosition={async (positionId, updates) => {
            // Map camelCase updates to the real positions columns (assigned_date, sold_date).
            const dbUpdates: any = {}
            if (updates.ticker !== undefined) dbUpdates.ticker = updates.ticker
            if (updates.quantity !== undefined) dbUpdates.quantity = updates.quantity
            if (updates.costBasis !== undefined) dbUpdates.cost_basis = updates.costBasis
            if (updates.acquisitionDate !== undefined) dbUpdates.assigned_date = updates.acquisitionDate
            if (updates.status !== undefined) dbUpdates.status = updates.status
            if (updates.closedDate !== undefined) dbUpdates.sold_date = updates.closedDate

            const { error } = await supabase.from('positions').update(dbUpdates).eq('id', positionId)
            if (error) {
              console.error('Failed to update position:', error)
              alert(`Failed to update position: ${error.message}`)
              return
            }
            // Optimistically merge the updates into local state (DB confirmed, no error).
            setPositions(prev => prev.map(p => p.id === positionId ? { ...p, ...updates } : p))
          }}
          onDeletePosition={async (positionId) => {
            if (!confirm('Delete this position?')) return
            const { error } = await supabase.from('positions').delete().eq('id', positionId)
            if (!error) {
              setPositions(prev => prev.filter(p => p.id !== positionId))
            }
          }}
          onLinkTrade={async (tradeId, positionId) => {
            const { error } = await supabase.from('trades').update({ position_id: positionId }).eq('id', tradeId)
            if (!error) {
              setData(d => {
                if (!d) return d
                return { ...d, trades: d.trades.map(t => t.id === tradeId ? { ...t, positionId } : t) }
              })
            }
          }}
          onUnlinkTrade={async (tradeId) => {
            const { error } = await supabase.from('trades').update({ position_id: null }).eq('id', tradeId)
            if (!error) {
              setData(d => {
                if (!d) return d
                return { ...d, trades: d.trades.map(t => t.id === tradeId ? { ...t, positionId: undefined } : t) }
              })
            }
          }}
        />

        {/* Position ticker filter chip */}
        {selectedPositionTicker && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-[var(--color-accent)]/10 text-[var(--color-accent)] border border-[var(--color-accent)]/30">
              Showing trades for: {selectedPositionTicker}
              <button
                onClick={() => setSelectedPositionTicker(null)}
                className="ml-1 hover:text-[var(--color-red)] transition-colors font-bold"
              >
                ✕
              </button>
            </span>
          </div>
        )}

        {/* ===== MOBILE: trade cards (< md) ===== */}
        <div className="md:hidden space-y-2">
          {displayTrades.length === 0 && (
            <div className="text-center text-[var(--color-text-muted)] py-10 text-sm">No trades.</div>
          )}
          {displayTrades.map(trade => (
            <TradeCard
              key={trade.id}
              trade={trade as TradeLike & { id: string }}
              expanded={expandedId === trade.id}
              onToggle={() => setExpandedId(expandedId === trade.id ? null : trade.id)}
              onClose={() => setClosingTrade(trade)}
              onDelete={() => handleDelete(trade.id)}
              onEdit={(field, value) => handleInlineEdit(trade.id, field, value)}
            />
          ))}
        </div>

        {/* ===== TABLET / LANDSCAPE: richer single-column rows (md to < lg) ===== */}
        <div className="hidden md:block lg:hidden space-y-1.5">
          {displayTrades.length === 0 && (
            <div className="text-center text-[var(--color-text-muted)] py-10 text-sm">No trades.</div>
          )}
          {displayTrades.map(trade => {
            const isOpen = expandedId === trade.id
            const cells = TABLET_ROW_FIELDS.map(k => formatTradeField(trade as TradeLike, k))
            const detailKeys = [...DETAIL_FIELDS, ...(trade.status !== 'open' ? CLOSED_DETAIL_FIELDS : [])]
            const toneCls = (tone?: string) => tone === 'pos' ? 'text-[var(--color-green)]' : tone === 'neg' ? 'text-[var(--color-red)]' : tone === 'muted' ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-text-primary)]'
            return (
              <div key={trade.id} className="border border-[var(--color-border)] rounded-lg bg-white">
                <button onClick={() => setExpandedId(isOpen ? null : trade.id)} aria-expanded={isOpen} className="w-full flex items-center gap-0 px-3 py-3 text-left">
                  {cells.map(f => (
                    <span key={f.key} className={`flex-1 min-w-0 ${f.key === 'ticker' ? 'font-bold text-[var(--color-accent)]' : ''}`}>
                      <span className="block text-[10px] text-[var(--color-text-muted)]">{f.label}</span>
                      <span className={`text-sm font-medium ${['netPnl','return'].includes(f.key) ? toneCls(f.tone) : ''}`}>{f.value}</span>
                    </span>
                  ))}
                  <span className="text-[var(--color-text-muted)] text-xs ml-2">{isOpen ? '▾' : '▸'}</span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 border-t border-dashed border-[var(--color-border)]">
                    <div className="grid grid-cols-3 gap-x-6 gap-y-2 pt-3" onClick={(e) => e.stopPropagation()}>
                      {detailKeys.map(key => {
                        const f = formatTradeField(trade as TradeLike, key)
                        const editable = EDITABLE_CARD_FIELDS[key]
                        return (
                          <div key={key}>
                            <span className="block text-[10px] text-[var(--color-text-muted)]">{f.label}</span>
                            {editable ? (
                              <InlineEdit
                                value={rawFieldValue(trade as TradeLike, key)}
                                fieldName={editable.modelField}
                                type={editable.type}
                                options={editable.type === 'select' ? [...CLOSE_ACTIONS] : undefined}
                                onSave={(v) => handleInlineEdit(trade.id, editable.modelField, v)}
                                displayFormatter={() => f.value}
                                className={`text-sm font-medium ${toneCls(f.tone)}`}
                              />
                            ) : (
                              <span className={`text-sm font-medium ${toneCls(f.tone)}`}>{f.value}</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <div className="flex gap-2 mt-3 max-w-sm">
                      {trade.status === 'open' && (
                        <button onClick={() => setClosingTrade(trade)} className="flex-1 h-10 rounded-lg bg-green-100 text-green-700 font-semibold text-sm">Close</button>
                      )}
                      <button onClick={() => handleDelete(trade.id)} className="flex-1 h-10 rounded-lg bg-red-50 text-[var(--color-red)] font-semibold text-sm border border-red-100">Delete</button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ===== DESKTOP: full table (lg+) ===== */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="text-sm border-collapse" style={{ minWidth: '1400px' }}>
            <thead className="bg-[var(--color-bg-tertiary)] sticky top-0">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  <input
                    type="checkbox"
                    checked={displayTrades.length > 0 && displayTrades.every(t => selectedIds.has(t.id))}
                    onChange={() => {
                      const allSelected = displayTrades.every(t => selectedIds.has(t.id))
                      if (allSelected) {
                        setSelectedIds(new Set())
                      } else {
                        setSelectedIds(new Set(displayTrades.map(t => t.id)))
                      }
                    }}
                    className="w-4 h-4 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                {columnOrder.map((colId, index) => {
                  const col = TRADE_COLUMNS.find(c => c.id === colId)
                  if (!col) return null
                  return (
                    <th
                      key={col.id}
                      className={`px-3 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase ${col.sortKey ? 'cursor-pointer hover:text-[var(--color-text-primary)]' : ''}`}
                      onClick={col.sortKey ? () => handleSort(col.sortKey!) : undefined}
                      {...dragHandlers(index)}
                    >
                      {col.label} {col.sortKey && sortCol === col.sortKey && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                  )
                })}
                <th className="px-3 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayTrades.map(trade => {
                // Calculate days to expiration for open trades
                const daysToExp = trade.status === 'open' && trade.expDate
                  ? (() => {
                      const [y, m, d] = trade.expDate.split('-').map(Number)
                      const exp = new Date(y, m - 1, d)  // Local date
                      const today = new Date()
                      today.setHours(0, 0, 0, 0)
                      return Math.round((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                    })()
                  : null
                const isExpiringSoon = daysToExp !== null && daysToExp <= 10 && daysToExp >= 0

                const renderCell = (colId: string) => {
                  switch (colId) {
                    case 'ticker':
                      return (
                        <td key={colId} className="px-3 py-2 font-medium text-[var(--color-accent)]">
                          <span className="inline-flex items-center">
                            <InlineEdit
                              value={trade.ticker}
                              fieldName="ticker"
                              type="text"
                              onSave={(v) => handleInlineEdit(trade.id, 'ticker', String(v).toUpperCase())}
                              className="font-medium text-[var(--color-accent)]"
                            />
                            <EarningsBadge ticker={trade.ticker} />
                          </span>
                        </td>
                      )
                    case 'action':
                      return (
                        <td key={colId} className="px-3 py-2">
                          <InlineEdit
                            value={trade.action}
                            fieldName="action"
                            type="select"
                            options={[
                              { label: 'CSP', value: 'CSP' },
                              { label: 'CC', value: 'CC' },
                            ]}
                            onSave={(v) => handleInlineEdit(trade.id, 'action', v)}
                            displayFormatter={(v) => String(v)}
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              trade.action === 'CSP' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                            }`}
                          />
                        </td>
                      )
                    case 'strike':
                      return (
                        <td key={colId} className="px-3 py-2 font-mono">
                          <InlineEdit
                            value={trade.strikePrice}
                            fieldName="strikePrice"
                            type="number"
                            onSave={(v) => handleInlineEdit(trade.id, 'strikePrice', v)}
                            displayFormatter={(v) => `$${v}`}
                            className="font-mono"
                          />
                        </td>
                      )
                    case 'bought':
                      return (
                        <td key={colId} className="px-3 py-2 font-mono text-[var(--color-text-muted)]">
                          <InlineEdit
                            value={trade.priceWhenBought || 0}
                            fieldName="priceWhenBought"
                            type="number"
                            onSave={(v) => handleInlineEdit(trade.id, 'priceWhenBought', v)}
                            displayFormatter={(v) => v ? `$${Number(v).toFixed(2)}` : '—'}
                            className="font-mono text-[var(--color-text-muted)]"
                          />
                        </td>
                      )
                    case 'current':
                      return (
                        <td key={colId} className="px-3 py-2 font-mono text-[var(--color-text-secondary)]">
                          {(() => {
                            const livePrice = livePrices[trade.ticker]
                            const displayPrice = livePrice || trade.currentPrice
                            return displayPrice ? `$${displayPrice.toFixed(2)}` : '\u2014'
                          })()}
                        </td>
                      )
                    case 'optionPrice':
                      return (
                        <td key={colId} className="px-3 py-2 font-mono text-[var(--color-accent)]">
                          {trade.currentOptionPrice ? `$${trade.currentOptionPrice.toFixed(2)}` : '—'}
                        </td>
                      )
                    case 'premium':
                      return (
                        <td key={colId} className="px-3 py-2 font-mono">
                          <InlineEdit
                            value={trade.premium}
                            fieldName="premium"
                            type="number"
                            onSave={(v) => handleInlineEdit(trade.id, 'premium', v)}
                            displayFormatter={(v) => `$${Number(v).toFixed(2)}`}
                            className="font-mono"
                          />
                        </td>
                      )
                    case 'qty':
                      return (
                        <td key={colId} className="px-3 py-2">
                          <InlineEdit
                            value={trade.qty}
                            fieldName="qty"
                            type="number"
                            onSave={(v) => handleInlineEdit(trade.id, 'qty', v)}
                          />
                        </td>
                      )
                    case 'total':
                      return (
                        <td key={colId} className="px-3 py-2 font-mono font-medium">${(trade.total ?? 0).toFixed(2)}</td>
                      )
                    case 'buyDate':
                      return (
                        <td key={colId} className="px-3 py-2 text-[var(--color-text-secondary)]">
                          <InlineEdit
                            value={trade.buyDate}
                            fieldName="buyDate"
                            type="date"
                            onSave={(v) => handleInlineEdit(trade.id, 'buyDate', v)}
                            displayFormatter={formatDateShort}
                            className="text-[var(--color-text-secondary)]"
                          />
                        </td>
                      )
                    case 'expDate':
                      return (
                        <td key={colId} className="px-3 py-2 text-[var(--color-text-secondary)]">
                          <InlineEdit
                            value={trade.expDate}
                            fieldName="expDate"
                            type="date"
                            onSave={(v) => handleInlineEdit(trade.id, 'expDate', v)}
                            displayFormatter={formatDateShort}
                            className="text-[var(--color-text-secondary)]"
                          />
                        </td>
                      )
                    case 'days':
                      return (
                        <td key={colId} className={`px-3 py-2 ${isExpiringSoon ? 'font-bold text-amber-700' : ''}`}>
                          {trade.status === 'open' && daysToExp !== null
                            ? <span className={daysToExp <= 3 ? 'text-[var(--color-red)] font-bold' : daysToExp <= 10 ? 'text-amber-600 font-semibold' : ''}>
                                {daysToExp}d
                              </span>
                            : (trade.daysKept || trade.daysLeft || '—')
                          }
                        </td>
                      )
                    case 'status':
                      return (
                        <td key={colId} className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            trade.status === 'open' ? 'bg-blue-100 text-blue-700' :
                            trade.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {trade.status}
                          </span>
                        </td>
                      )
                    case 'itmOtm':
                      return (
                        <td key={colId} className="px-3 py-2">
                          {trade.status === 'open' && (() => {
                            const price = livePrices[trade.ticker] || trade.currentPrice
                            if (!price || price <= 0) return <span className="text-[var(--color-text-muted)]">—</span>
                            const isITM = trade.action === 'CSP'
                              ? price < trade.strikePrice
                              : price > trade.strikePrice
                            return (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                isITM ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {isITM ? 'ITM' : 'OTM'}
                              </span>
                            )
                          })()}
                          {trade.status !== 'open' && <span className="text-[var(--color-text-muted)]">—</span>}
                        </td>
                      )
                    case 'closeAction':
                      return (
                        <td key={colId} className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{trade.closeAction || '—'}</td>
                      )
                    case 'netPnl':
                      return (
                        <td key={colId} className={`px-3 py-2 font-mono font-medium ${
                          trade.netPosition > 0 ? 'text-[var(--color-green)]' :
                          trade.netPosition < 0 ? 'text-[var(--color-red)]' : 'text-[var(--color-text-muted)]'
                        }`}>
                          {trade.status === 'open' ? '—' : (trade.netPosition ? `$${trade.netPosition.toFixed(2)}` : '—')}
                        </td>
                      )
                    case 'return':
                      return (
                        <td key={colId} className={`px-3 py-2 font-mono ${
                          trade.returnPct > 0 ? 'text-[var(--color-green)]' :
                          trade.returnPct < 0 ? 'text-[var(--color-red)]' : 'text-[var(--color-text-muted)]'
                        }`}>
                          {trade.status === 'open' ? '—' : (trade.returnPct ? `${trade.returnPct.toFixed(2)}%` : '—')}
                        </td>
                      )
                    default:
                      return <td key={colId} className="px-3 py-2">—</td>
                  }
                }

                return (
                <React.Fragment key={trade.id}>
                <tr
                  className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]/50 group cursor-pointer ${
                    isExpiringSoon ? 'bg-amber-50 border-l-4 border-l-amber-500' :
                    trade.status === 'open' ? 'bg-blue-50' : ''
                  } ${expandedId === trade.id ? 'bg-[var(--color-bg-tertiary)]' : ''}`}
                  style={{ height: '48px' }}
                  onClick={() => setExpandedId(expandedId === trade.id ? null : trade.id)}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(trade.id)}
                      onChange={() => {
                        setSelectedIds(prev => {
                          const next = new Set(prev)
                          if (next.has(trade.id)) next.delete(trade.id)
                          else next.add(trade.id)
                          return next
                        })
                      }}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                  {columnOrder.map(colId => renderCell(colId))}
                  <td className="px-3 py-2">
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {trade.status === 'open' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setClosingTrade(trade) }}
                          className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200"
                        >
                          Close
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(trade.id) }}
                        className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
                {/* Expandable detail row */}
                {expandedId === trade.id && (
                  <tr className="bg-[var(--color-bg-tertiary)]/70">
                    <td colSpan={columnOrder.length + 2} className="px-6 py-4">
                      <div className="grid grid-cols-3 gap-x-12">
                        <div className="min-w-[280px]">
                          <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-3">Open Details</h4>
                          <table className="w-full text-sm">
                            <tbody>
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Price When Bought</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.priceWhenBought || 0} fieldName="priceWhenBought" type="number" onSave={(v) => handleInlineEdit(trade.id, 'priceWhenBought', v)} displayFormatter={(v) => v ? `$${Number(v).toFixed(2)}` : '—'} className="font-mono" /></td></tr>
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Premium</td><td className="py-1 text-right font-mono">${(trade.premium ?? 0).toFixed(2)} × {trade.qty}</td></tr>
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Commission</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.commission} fieldName="commission" type="number" onSave={(v) => handleInlineEdit(trade.id, 'commission', v)} displayFormatter={(v) => `$${Number(v).toFixed(2)}`} className="font-mono" /></td></tr>
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Fees</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.fees} fieldName="fees" type="number" onSave={(v) => handleInlineEdit(trade.id, 'fees', v)} displayFormatter={(v) => `$${Number(v).toFixed(2)}`} className="font-mono" /></td></tr>
                              <tr className="border-t border-[var(--color-border)]"><td className="py-1 text-[var(--color-text-muted)] font-medium whitespace-nowrap pr-4">Total Received</td><td className="py-1 text-right font-mono font-medium text-[var(--color-green)]">${(trade.total ?? 0).toFixed(2)}</td></tr>
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Original Days</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.originalDays} fieldName="originalDays" type="number" onSave={(v) => handleInlineEdit(trade.id, 'originalDays', v)} displayFormatter={(v) => String(v || '—')} className="font-mono" /></td></tr>
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Delta</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.delta ?? 0} fieldName="delta" type="number" onSave={(v) => handleInlineEdit(trade.id, 'delta', v)} displayFormatter={(v) => v ? Number(v).toFixed(4) : '—'} className="font-mono" /></td></tr>
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">IV</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.iv ?? 0} fieldName="iv" type="number" onSave={(v) => handleInlineEdit(trade.id, 'iv', v)} displayFormatter={(v) => v ? `${Number(v).toFixed(1)}%` : '—'} className="font-mono" /></td></tr>
                              {trade.delta != null && trade.delta !== 0 && (
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">POP</td><td className="py-1 text-right font-mono">{((1 - Math.abs(trade.delta)) * 100).toFixed(1)}%</td></tr>
                              )}
                              <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Break-even</td><td className="py-1 text-right font-mono">{trade.action === 'CSP' ? `$${(trade.strikePrice - trade.premium).toFixed(2)}` : `$${(trade.strikePrice + trade.premium).toFixed(2)}`}</td></tr>
                              {trade.daysKept != null && trade.daysKept > 0 && trade.returnPct !== 0 && (
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Annualized Return</td><td className="py-1 text-right font-mono">{((trade.returnPct / trade.daysKept) * 365).toFixed(1)}%</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {trade.status !== 'open' ? (
                          <div className="min-w-[280px]">
                            <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-3">Close Details</h4>
                            <table className="w-full text-sm">
                              <tbody>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Price When Closed</td><td className="py-1 text-right font-mono">{trade.currentPrice ? `$${trade.currentPrice.toFixed(2)}` : '—'}</td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Close Action</td><td className="py-1 text-right" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.closeAction || ''} fieldName="closeAction" type="select" options={[...CLOSE_ACTIONS]} onSave={(v) => handleInlineEdit(trade.id, 'closeAction', v)} displayFormatter={(v) => String(v) || '—'} className="font-medium" /></td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Close Date</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.closeDate || ''} fieldName="closeDate" type="date" onSave={(v) => handleInlineEdit(trade.id, 'closeDate', v)} displayFormatter={formatDateShort} className="font-mono" /></td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Buy Back Premium</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.closePremium ?? 0} fieldName="closePremium" type="number" onSave={(v) => handleInlineEdit(trade.id, 'closePremium', v)} displayFormatter={(v) => v ? `$${Number(v).toFixed(2)}` : '—'} className="font-mono" /></td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Commission</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.closeCommission} fieldName="closeCommission" type="number" onSave={(v) => handleInlineEdit(trade.id, 'closeCommission', v)} displayFormatter={(v) => `$${Number(v).toFixed(2)}`} className="font-mono" /></td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Fees</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.closeFees} fieldName="closeFees" type="number" onSave={(v) => handleInlineEdit(trade.id, 'closeFees', v)} displayFormatter={(v) => `$${Number(v).toFixed(2)}`} className="font-mono" /></td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Close Total</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.closeTotal ?? 0} fieldName="closeTotal" type="number" onSave={(v) => handleInlineEdit(trade.id, 'closeTotal', v)} displayFormatter={(v) => v ? `$${Number(v).toFixed(2)}` : '—'} className="font-mono text-[var(--color-red)]" /></td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] whitespace-nowrap pr-4">Days Kept</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.daysKept ?? 0} fieldName="daysKept" type="number" onSave={(v) => handleInlineEdit(trade.id, 'daysKept', v)} displayFormatter={(v) => v ? String(v) : '—'} className="font-mono" /></td></tr>
                                <tr className="border-t border-[var(--color-border)]"><td className="py-1 text-[var(--color-text-muted)] font-medium whitespace-nowrap pr-4">Net P&L</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.netPosition ?? 0} fieldName="netPosition" type="number" onSave={(v) => handleInlineEdit(trade.id, 'netPosition', v)} displayFormatter={(v) => `$${Number(v).toFixed(2)}`} className={`font-mono font-medium ${trade.netPosition > 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`} /></td></tr>
                                <tr><td className="py-1 text-[var(--color-text-muted)] font-medium whitespace-nowrap pr-4">Return %</td><td className="py-1 text-right font-mono" onClick={(e) => e.stopPropagation()}><InlineEdit value={trade.returnPct ?? 0} fieldName="returnPct" type="number" onSave={(v) => handleInlineEdit(trade.id, 'returnPct', v)} displayFormatter={(v) => `${Number(v).toFixed(2)}%`} className={`font-mono font-medium ${trade.returnPct > 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`} /></td></tr>
                                {trade.total !== 0 && (
                                  <tr><td className="py-1 text-[var(--color-text-muted)] font-medium whitespace-nowrap pr-4">Net P/L %</td><td className={`py-1 text-right font-mono font-bold ${trade.netPosition > 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>{trade.total !== 0 ? `${((trade.netPosition / Math.abs(trade.total)) * 100).toFixed(1)}%` : '—'}</td></tr>
                                )}
                                <tr><td colSpan={2} className="pt-3" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleRecalculate(trade.id)}
                                    className="w-full px-3 py-1.5 text-xs font-medium rounded bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
                                  >
                                    🔄 Recalculate Totals & P&L
                                  </button>
                                </td></tr>
                              </tbody>
                            </table>
                          </div>
                        ) : <div />}
                        <div>
                          <label className="text-xs font-semibold text-[var(--color-text-muted)] uppercase">Notes</label>
                          <textarea
                            defaultValue={trade.notes || ''}
                            placeholder="Add notes..."
                            rows={4}
                            className="mt-1 w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)] resize-none focus:border-[var(--color-accent)] focus:outline-none"
                            onClick={(e) => e.stopPropagation()}
                            onBlur={async (e) => {
                              const newNotes = e.target.value
                              if (newNotes !== (trade.notes || '')) {
                                await handleInlineEdit(trade.id, 'notes', newNotes)
                              }
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              )})}
            </tbody>
            {displayTrades.length > 0 && (
              <tfoot className="bg-[var(--color-bg-tertiary)] border-t-2 border-[var(--color-border)] sticky bottom-0">
                <tr style={{ height: '44px' }}>
                  <td className="px-3 py-2"></td>
                  {columnOrder.map((colId, index) => {
                    switch (colId) {
                      case 'ticker':
                        return (
                          <td key={colId} className="px-3 py-2 font-bold text-[var(--color-text-primary)]">
                            Summary ({displayTrades.length})
                          </td>
                        )
                      case 'premium':
                        return (
                          <td key={colId} className="px-3 py-2 font-mono text-[var(--color-text-secondary)]">
                            avg ${(displayTrades.reduce((s, t) => s + t.premium, 0) / displayTrades.length).toFixed(2)}
                          </td>
                        )
                      case 'qty':
                        return (
                          <td key={colId} className="px-3 py-2 text-[var(--color-text-secondary)]">
                            {displayTrades.reduce((s, t) => s + t.qty, 0)}
                          </td>
                        )
                      case 'total':
                        return (
                          <td key={colId} className="px-3 py-2 font-mono font-bold text-[var(--color-text-primary)]">
                            ${displayTrades.reduce((s, t) => s + t.total, 0).toFixed(2)}
                          </td>
                        )
                      case 'netPnl':
                        return (
                          <td key={colId} className={`px-3 py-2 font-mono font-bold ${
                            displayTrades.filter(t => t.status !== 'open').reduce((s, t) => s + (t.netPosition || 0), 0) >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'
                          }`}>
                            ${displayTrades.filter(t => t.status !== 'open').reduce((s, t) => s + (t.netPosition || 0), 0).toFixed(2)}
                          </td>
                        )
                      case 'return':
                        const tradesWithReturn = displayTrades.filter(t => t.status !== 'open' && t.returnPct)
                        const avgReturn = tradesWithReturn.reduce((s, t) => s + t.returnPct, 0) / (tradesWithReturn.length || 1)
                        // Annualized: weighted by daysKept, then annualize
                        const tradesWithDays = displayTrades.filter(t => t.status !== 'open' && t.returnPct && t.daysKept && t.daysKept > 0)
                        const totalWeightedReturn = tradesWithDays.reduce((s, t) => s + (t.returnPct / (t.daysKept || 1)) * 365, 0)
                        const annualizedReturn = tradesWithDays.length > 0 ? totalWeightedReturn / tradesWithDays.length : 0
                        const displayReturn = returnMode === 'annualized' ? annualizedReturn : avgReturn
                        return (
                          <td key={colId} className={`px-3 py-2 font-mono font-bold cursor-pointer ${
                            displayReturn >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'
                          }`} onClick={() => setReturnMode(returnMode === 'average' ? 'annualized' : 'average')} title="Click to toggle between average and annualized return">
                            {returnMode === 'annualized' ? 'ann' : 'avg'} {displayReturn.toFixed(2)}%
                          </td>
                        )
                      default:
                        return <td key={colId} className="px-3 py-2"></td>
                    }
                  })}
                  <td className="px-3 py-2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {filteredTrades.length === 0 && (
          <div className="text-center py-12 text-[var(--color-text-muted)]">
            <p className="text-lg mb-2">No {filter !== 'all' ? filter : ''} trades found</p>
          </div>
        )}
      </div>

      {/* Floating action bar for bulk delete */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-6 py-3 bg-white rounded-xl shadow-2xl border border-[var(--color-border)]">
          <span className="text-sm font-medium text-[var(--color-text-primary)]">
            {selectedIds.size} selected
          </span>
          <button
            onClick={handleBulkDelete}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
          >
            Delete {selectedIds.size} selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-2 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Mobile filter bottom sheet */}
      {showFilterSheet && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end bg-black/45" onClick={() => setShowFilterSheet(false)}>
          <div className="w-full bg-white rounded-t-2xl p-4 safe-bottom animate-sheet-up max-h-[80%] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold">Filter trades</h3>
              <button onClick={() => setShowFilterSheet(false)} className="text-xl text-[var(--color-text-muted)]">✕</button>
            </div>
            <label className="block text-xs font-semibold text-[var(--color-text-muted)] mb-1">Ticker</label>
            <select value={tickerFilter} onChange={e => setTickerFilter(e.target.value)} className="w-full h-12 rounded-xl border border-[var(--color-border)] px-3 mb-4 bg-white">
              <option value="">All Tickers</option>
              {Array.from(new Set(data.trades.map(t => t.ticker))).sort().map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="block text-xs font-semibold text-[var(--color-text-muted)] mb-1">Opened in</label>
            <select value={openedInMonth} onChange={e => setOpenedInMonth(e.target.value)} className="w-full h-12 rounded-xl border border-[var(--color-border)] px-3 mb-4 bg-white">
              <option value="">All</option>
              {Array.from(new Set(data.trades.map(t => t.buyDate?.slice(0, 7)).filter(Boolean))).sort().map(ym => {
                const [y, m] = ym!.split('-'); return <option key={ym} value={ym}>{new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</option>
              })}
            </select>
            <label className="block text-xs font-semibold text-[var(--color-text-muted)] mb-1">Closed in</label>
            <select value={closedInMonth} onChange={e => setClosedInMonth(e.target.value)} className="w-full h-12 rounded-xl border border-[var(--color-border)] px-3 mb-4 bg-white">
              <option value="">All</option>
              {Array.from(new Set(data.trades.filter(t => t.closeDate).map(t => t.closeDate!.slice(0, 7)))).sort().map(ym => {
                const [y, m] = ym.split('-'); return <option key={ym} value={ym}>{new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</option>
              })}
            </select>
            <div className="flex gap-2">
              <button onClick={() => { setTickerFilter(''); setOpenedInMonth(''); setClosedInMonth('') }} className="flex-1 h-12 rounded-xl border border-[var(--color-border)] font-semibold">Clear</button>
              <button onClick={() => setShowFilterSheet(false)} className="flex-1 h-12 rounded-xl bg-[var(--color-accent)] text-white font-semibold">Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showNewForm && (
        <NewTradeForm
          accountSlug={accountSlug || 'wheel'}
          positions={positions}
          initialData={newTradeInitialData}
          onSubmit={handleNewTrade}
          onCancel={() => { setShowNewForm(false); setNewTradeInitialData(undefined) }}
        />
      )}
      {closingTrade && (
        <CloseTradeForm
          trade={closingTrade}
          positions={positions}
          accountSlug={accountSlug || ''}
          onSubmit={handleCloseTrade}
          onCancel={() => setClosingTrade(null)}
        />
      )}
      {closingLeap && (
        <CloseLeapModal
          leap={closingLeap}
          onSubmit={handleCloseLeap}
          onCancel={() => setClosingLeap(null)}
        />
      )}
      {showMonthlyPnL && (() => {
        const closedTrades = data.trades.filter(t => t.status !== 'open' && t.closeDate)

        // Include position sale appreciation (closed positions with sold_price)
        const soldPositions = positions.filter(p => p.status === 'closed' && (p as any).soldPrice && (p as any).soldDate)

        // Get available years dynamically from trade close dates + position sold dates
        const allDates = [
          ...closedTrades.map(t => (t.closeDate || '').substring(0, 4)),
          ...soldPositions.map(p => ((p as any).soldDate || '').substring(0, 4)),
        ]
        const availableYears = Array.from(new Set(
          allDates.filter(y => y && y !== '0000')
        )).sort().reverse()

        // Filter trades by selected year
        const closedForPnL = pnlYearFilter === 'all'
          ? closedTrades
          : closedTrades.filter(t => (t.closeDate || '').startsWith(pnlYearFilter))

        // Filter sold positions by selected year
        const soldForPnL = pnlYearFilter === 'all'
          ? soldPositions
          : soldPositions.filter(p => ((p as any).soldDate || '').startsWith(pnlYearFilter))

        const monthlyPnL: Record<string, { pnl: number; count: number }> = {}
        closedForPnL.forEach(t => {
          const [y, m] = (t.closeDate || t.buyDate).split('-').map(Number)
          const key = `${y}-${String(m).padStart(2, '0')}`
          if (!monthlyPnL[key]) monthlyPnL[key] = { pnl: 0, count: 0 }
          monthlyPnL[key].pnl += (t.netPosition || 0)
          monthlyPnL[key].count += 1
        })
        // Add position sale appreciation to monthly P&L
        soldForPnL.forEach(p => {
          const soldDate = (p as any).soldDate || ''
          if (!soldDate) return
          const [y, m] = soldDate.split('-').map(Number)
          const key = `${y}-${String(m).padStart(2, '0')}`
          if (!monthlyPnL[key]) monthlyPnL[key] = { pnl: 0, count: 0 }
          const sharesSold = (p as any).sharesSold || (p as any).quantity || 0
          const appreciation = ((p as any).soldPrice - p.costBasis) * sharesSold
          monthlyPnL[key].pnl += appreciation
          monthlyPnL[key].count += 1
        })
        const months = Object.entries(monthlyPnL).sort((a, b) => {
          if (pnlSortCol === 'month') {
            return pnlSortDir === 'asc' ? a[0].localeCompare(b[0]) : b[0].localeCompare(a[0])
          }
          return pnlSortDir === 'asc' ? a[1].pnl - b[1].pnl : b[1].pnl - a[1].pnl
        })
        const handlePnlSort = (col: 'month' | 'pnl') => {
          if (pnlSortCol === col) setPnlSortDir(d => d === 'asc' ? 'desc' : 'asc')
          else { setPnlSortCol(col); setPnlSortDir('desc') }
        }
        const formatMonth = (key: string) => {
          const [year, month] = key.split('-')
          const date = new Date(Number(year), Number(month) - 1)
          return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        }
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowMonthlyPnL(false)}>
            <div className="bg-white rounded-2xl shadow-2xl border border-[var(--color-border)] w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
                <h2 className="text-lg font-bold text-[var(--color-text-primary)]">Monthly P&L</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={pnlYearFilter}
                    onChange={e => setPnlYearFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-lg bg-white"
                  >
                    <option value="all">All Time</option>
                    {availableYears.map(yr => <option key={yr} value={yr}>{yr}</option>)}
                  </select>
                  <button onClick={() => setShowMonthlyPnL(false)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] text-xl">&times;</button>
                </div>
              </div>
              <div className="overflow-auto max-h-[60vh] px-6 py-4">
                {months.length === 0 ? (
                  <p className="text-[var(--color-text-muted)] text-center py-8">No closed trades yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--color-border)]">
                        <th className="text-left py-2 text-xs font-semibold text-[var(--color-text-muted)] uppercase cursor-pointer hover:text-[var(--color-text-primary)]" onClick={() => handlePnlSort('month')}>Month {pnlSortCol === 'month' && (pnlSortDir === 'asc' ? '↑' : '↓')}</th>
                        <th className="text-right py-2 text-xs font-semibold text-[var(--color-text-muted)] uppercase cursor-pointer hover:text-[var(--color-text-primary)]" onClick={() => handlePnlSort('pnl')}>Net P&L {pnlSortCol === 'pnl' && (pnlSortDir === 'asc' ? '↑' : '↓')}</th>
                        <th className="text-right py-2 text-xs font-semibold text-[var(--color-text-muted)] uppercase"># Trades</th>
                      </tr>
                    </thead>
                    <tbody>
                      {months.map(([month, { pnl, count }]) => (
                        <tr key={month} className="border-b border-[var(--color-border)]/50">
                          <td className="py-2 text-[var(--color-text-primary)]">{formatMonth(month)}</td>
                          <td className={`py-2 text-right font-mono font-medium ${pnl >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>${pnl.toFixed(2)}</td>
                          <td className="py-2 text-right text-[var(--color-text-secondary)]">{count}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[var(--color-border)]">
                        <td className="py-2 font-bold text-[var(--color-text-primary)]">Total</td>
                        <td className={`py-2 text-right font-mono font-bold ${months.reduce((s, [, v]) => s + v.pnl, 0) >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>${months.reduce((s, [, v]) => s + v.pnl, 0).toFixed(2)}</td>
                        <td className="py-2 text-right font-bold text-[var(--color-text-secondary)]">{months.reduce((s, [, v]) => s + v.count, 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        )
      })()}
      {showPremiumByTicker && (
        <PremiumByTickerModal
          trades={data.trades}
          positions={positions.map(p => ({ ticker: p.ticker, status: p.status, costBasis: p.costBasis, soldPrice: (p as any).soldPrice, soldDate: (p as any).soldDate, sharesSold: (p as any).sharesSold }))}
          onClose={() => setShowPremiumByTicker(false)}
        />
      )}
    </div>
  )
}

// === Close LEAP Modal ===
function CloseLeapModal({ leap, onSubmit, onCancel }: {
  leap: Trade
  onSubmit: (leap: Trade, sellPremium: number, closeCommission: number, closeFees: number, closeDate: string) => Promise<void>
  onCancel: () => void
}) {
  const [sellPremium, setSellPremium] = useState('')
  const [commission, setCommission] = useState('0.65')
  const [fees, setFees] = useState('0.02')
  const [closeDate, setCloseDate] = useState(new Date().toISOString().split('T')[0])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const qty = Number(leap.qty) || 0
  const preview = sellPremium
    ? (parseFloat(sellPremium) * 100 * qty) - Math.abs(parseFloat(commission) || 0) - Math.abs(parseFloat(fees) || 0) + (Number(leap.total) || 0)
    : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const price = parseFloat(sellPremium)
    if (!price || price <= 0) { setError('Sell premium is required'); return }
    if (!closeDate) { setError('Close date is required'); return }

    setSubmitting(true)
    try {
      await onSubmit(leap, price, parseFloat(commission) || 0, parseFloat(fees) || 0, closeDate)
    } catch {
      setError('Failed to close LEAP')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-[var(--color-text-primary)]">Close LEAP</h2>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">
          {leap.ticker} ${leap.strikePrice} Call — {leap.qty} contract{qty !== 1 ? 's' : ''} — Cost: ${Math.abs(leap.total ?? 0).toFixed(2)}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[var(--color-text-muted)]">Sell Premium Per Share</label>
            <input
              type="number"
              step="0.01"
              value={sellPremium}
              onChange={e => setSellPremium(e.target.value)}
              placeholder="e.g. 28.50"
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)]">Commission</label>
              <input
                type="number"
                step="0.01"
                value={commission}
                onChange={e => setCommission(e.target.value)}
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)]">Fees</label>
              <input
                type="number"
                step="0.01"
                value={fees}
                onChange={e => setFees(e.target.value)}
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[var(--color-text-muted)]">Close Date</label>
            <input
              type="date"
              value={closeDate}
              onChange={e => setCloseDate(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
            />
          </div>

          {preview !== null && parseFloat(sellPremium) > 0 && (
            <div className={`px-3 py-2 rounded-lg border text-sm ${
              preview >= 0
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              Net P&L: <span className="font-mono font-bold">${preview.toFixed(2)}</span>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
            <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Closing...' : 'Close LEAP'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
